/**
 * Lazy-autoplay videos that have data-lazy-video.
 *
 * Background: the homepage previously rendered ~8 autoplay videos at initial
 * paint. Each `<video autoplay>` forces the browser to download the entire
 * file even when `preload="metadata"` is set. Net effect: ~31 MB initial
 * payload, LCP > 18s on mobile.
 *
 * Strategy:
 *   - In Liquid, render these videos with `preload="none"` and WITHOUT
 *     `autoplay`. Add `data-lazy-video`.
 *   - This script attaches an IntersectionObserver and starts playback when
 *     each video crosses ~25% visibility, pausing when it scrolls fully out
 *     of view. The browser only fetches video bytes once playback starts.
 *
 * Idempotent: safe to load multiple times; observer is per-element via WeakSet.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var observed = new WeakSet();

  function attemptPlay(video) {
    // muted+playsinline are required for autoplay-on-scroll to satisfy
    // browser autoplay policies. Liquid sets both, but enforce here too.
    if (!video.muted) video.muted = true;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () { /* ignore — autoplay blocked or interrupted */ });
    }
  }

  function fallbackPlayAll() {
    document.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
      attemptPlay(v);
    });
  }

  function init() {
    if (!('IntersectionObserver' in window)) {
      fallbackPlayAll();
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          attemptPlay(v);
        } else if (!entry.isIntersecting) {
          if (!v.paused) {
            try { v.pause(); } catch (_) { /* noop */ }
          }
        }
      });
    }, { threshold: [0, 0.25, 0.5] });

    document.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
      if (observed.has(v)) return;
      observed.add(v);
      io.observe(v);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
